import {faLock} from "@fortawesome/pro-solid-svg-icons/faLock";
import {MailFile, MailFileThumbnailType} from "@twigsby/client-data/lib/Types/MailFileTypes";
import {Button, ColourSolid, useTranslation} from "@twigsby/frontend-framework";
import React, {useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faImage} from "@fortawesome/pro-solid-svg-icons/faImage";
import {faCreditCard} from "@fortawesome/pro-solid-svg-icons/faCreditCard";
import {faEnvelope} from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import {Blurhash} from "react-blurhash";

export enum MailPartTypeEnum {
	'Mail-Envelopes'= 1,
	'Mail-Contents'= 2,
}

export enum ContentsTypeEnum {
	'Mail-Envelopes',
	'Mail-Contents',
	'Mail-Sends'
}

type MailItemThumbsType = {
	files: Array<MailFile>;
	mailType: MailPartTypeEnum;
	onClickCallback: (link: string) => void;
	hideMissing?: boolean;
	largeThumbnail?: boolean;
}

type MailThumbnailPropsType = {
	file: MailFile;
	mailType: MailPartTypeEnum;
	onClickCallback: (link: string) => void;
	largeThumbnail?: boolean;
	hideMissing?: boolean;
}

interface MailFileThumbnailCustomType extends MailFileThumbnailType {
	largeThumbnail?: boolean;
	onClickCallback?: (link: string) => void;
}

const BuildMailItemThumbnail = (props: MailThumbnailPropsType) => {
	if(props.file.isLocked) {
		return <MailItemLocked largeThumbnail={props.largeThumbnail} />
	}

	if(props.file.topupRequired) {
		return <MailItemTopUpRequired largeThumbnail={props.largeThumbnail} />
	}

	if(!!props.file.userFileThumbnails && props.file.userFileThumbnails.length > 0 && props.file.userFileThumbnails[0]?.linkToFile) {
		return <MailItemThumbnail
			{...props.file.userFileThumbnails[props.largeThumbnail ? props.file.userFileThumbnails.length - 1 : 0]}
			largeThumbnail={props.largeThumbnail}
			onClickCallback={(link) => props.onClickCallback(link)}
		/>
	}

	if(!props.hideMissing) {
		return <MailItemNoThumbnail mailType={props.mailType} largeThumbnail={props.largeThumbnail} />
	}

	return <></>;
}

const MailItemThumbnails = (props: MailItemThumbsType) => {
	const MappedThumbnailCollection = props.files?.flatMap((file, index) => {
		if(file.mailPart !== props.mailType) return [];

		return (
			<BuildMailItemThumbnail
				key={`mailItem${props.mailType}Thumb${index}`}
				file={file}
				mailType={props.mailType}
				hideMissing={props.hideMissing}
				largeThumbnail={props.largeThumbnail}
				onClickCallback={(link) => props.onClickCallback(link)}
			/>
		)
	});

	if(MappedThumbnailCollection?.length === 0 && !props.hideMissing) {
		if(props.files[0]?.topupRequired) {
			return (
				<MailItemTopUpRequired largeThumbnail={props.largeThumbnail} />
			)
		}

		return (
			<MailItemNoThumbnail mailType={props.mailType} largeThumbnail={props.largeThumbnail} />
		)
	}

	if(MappedThumbnailCollection?.length > 0) {
		return <>{MappedThumbnailCollection}</>
	}

	return null;
}


const MailItemThumbnail = (props: MailFileThumbnailCustomType) => {
	const [ imageLoaded, setImageLoaded] = useState<boolean>(false);

	return (
		<div
			className={`blur-hash-wrapper item-thumbnail ${props.onClickCallback ? 'mail-item-thumbnail-hover' : ''} ${props.largeThumbnail ? 'item-large-thumbnail' : ''}`}
			onClick={() => props.onClickCallback ? props.onClickCallback(props.linkToFile) : null}
			style={props.largeThumbnail ? { aspectRatio: `${props.fileWidth}/${props.fileHeight}`} : {}}
		>
			<img
				alt={props.fileName}
				src={props.linkToFile}
				loading="lazy"
				onLoad={() => setImageLoaded(true)}
			/>
			{props.blurHash && (
				<Blurhash
					resolutionX={props.largeThumbnail ? 12 : 8}
					resolutionY={props.largeThumbnail ? 12 : 8}
					hash={props.blurHash}
					width={'100%'}
					height={'100%'}
					style={{ opacity: imageLoaded ? 0 : 1}}
				/>
			)}
		</div>
	);
};


type MailItemNoThumbnailType = {
	mailType: MailPartTypeEnum;
	largeThumbnail?: boolean;
}

const MailItemNoThumbnail = (props: MailItemNoThumbnailType) => {
	return(
		<div className={`item-thumbnail no-thumbnail ${props.largeThumbnail ? 'no-thumbnail-large' : ''}`}>
			{props.mailType === MailPartTypeEnum["Mail-Envelopes"] && (
				<FontAwesomeIcon icon={faEnvelope} size={props.largeThumbnail ? "4x" : "1x"} />
			)}
			{props.mailType === MailPartTypeEnum["Mail-Contents"] && (
				<FontAwesomeIcon icon={faImage} size={props.largeThumbnail ? "4x" : "1x"} />
			)}
		</div>
	)
}

type MailItemTopUpRequiredType = {
	largeThumbnail?: boolean;
}

const MailItemTopUpRequired = (props: MailItemTopUpRequiredType) => {
	return(
		<div className={`item-thumbnail no-thumbnail ${props.largeThumbnail ? 'no-thumbnail-large' : ''} requires-top-up`}>
			<FontAwesomeIcon icon={faCreditCard} size={props.largeThumbnail ? "3x" : "1x"} />
		</div>
	)
}

type MailItemLockedType = {
	largeThumbnail?: boolean;
}

const MailItemLocked = (props: MailItemLockedType) => {
	return (
		<div className={`item-thumbnail no-thumbnail ${props.largeThumbnail ? 'no-thumbnail-large' : ''} requires-action`}>
			<FontAwesomeIcon icon={faLock} size={props.largeThumbnail ? "3x" : "1x"} />
		</div>
	)
}

export {MailItemThumbnails, MailItemThumbnail, MailItemNoThumbnail, MailItemTopUpRequired }
